import { getCurrentAppLocale } from "@/i18n";
function asRecord(value) {
    return value && typeof value === "object" ? value : {};
}
function asString(value) {
    return typeof value === "string" && value.trim() ? value : undefined;
}
function asNumber(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
function normalizePhotos(service) {
    const urlCandidates = service.photoUrls ?? service.photo_urls;
    if (Array.isArray(urlCandidates)) {
        return urlCandidates
            .filter((url) => typeof url === "string" && url.length > 0)
            .map((image_url) => ({ image_url }));
    }
    const photos = service.photos;
    if (!Array.isArray(photos))
        return [];
    return photos
        .map((photo) => {
        if (typeof photo === "string")
            return photo;
        const record = asRecord(photo);
        return asString(record.image_url ?? record.photoUrl ?? record.photo_url);
    })
        .filter((image_url) => Boolean(image_url))
        .map((image_url) => ({ image_url }));
}
function normalizeCustomFieldsSchema(value) {
    const source = typeof value === "string" ? parseJson(value) : value;
    if (!Array.isArray(source))
        return [];
    return source
        .map((field) => asRecord(field))
        .filter((field) => typeof field.key === "string" && field.key.length > 0)
        .map((field) => ({
        ...field,
        key: field.key,
        label: asString(field.label),
    }));
}
function parseJson(value) {
    try {
        return JSON.parse(value);
    }
    catch {
        return [];
    }
}
function normalizeBookingItem(item) {
    const source = asRecord(item);
    const service = asRecord(source.service);
    const serviceType = asRecord(service.service_type);
    return {
        ...source,
        service: {
            ...service,
            service_type: Object.keys(serviceType).length
                ? {
                    ...serviceType,
                    custom_fields_schema: normalizeCustomFieldsSchema(serviceType.custom_fields_schema),
                }
                : null,
            photos: normalizePhotos(service),
        },
    };
}
function normalizeConcierge(source) {
    const concierge = asRecord(source.concierge ?? source.creator ?? source.created_by ?? source.createdBy);
    if (Object.keys(concierge).length === 0)
        return null;
    return {
        id: asNumber(concierge.id),
        name: asString(concierge.name) ??
            asString(concierge.assigned_person) ??
            asString(concierge.company_name),
        assigned_person: asString(concierge.assigned_person),
        company_name: asString(concierge.company_name),
        email: asString(concierge.email) ?? null,
        contact_phone: asString(concierge.contact_phone) ?? null,
        image_url: asString(concierge.image_url) ?? null,
    };
}
function normalizeBrand(source) {
    const brand = asRecord(source.brand);
    if (Object.keys(brand).length === 0)
        return undefined;
    return {
        name: asString(brand.name),
        documentTitle: asString(brand.documentTitle),
        creator: asString(brand.creator),
        producer: asString(brand.producer),
        logoUrl: asString(brand.logoUrl ??
            brand.logo_url ??
            brand.publicLogoUrl ??
            brand.public_logo_url) ?? null,
    };
}
export function mapItineraryPdfProjection(response) {
    const root = asRecord(response);
    const itinerarySource = asRecord(root.itinerary ?? root);
    const rawItems = itinerarySource.slots_itineraries ?? itinerarySource.itinerary_tour ?? [];
    const slots = Array.isArray(rawItems) ? rawItems.map(normalizeBookingItem) : [];
    const itinerary = {
        ...itinerarySource,
        id: asNumber(itinerarySource.id) ?? 0,
        code: asString(itinerarySource.code) ?? "ITN-0000",
        customer: asRecord(itinerarySource.customer),
        slots_itineraries: slots,
        itinerary_tour: slots,
    };
    return {
        itinerary,
        concierge: normalizeConcierge(root) ?? normalizeConcierge(itinerarySource),
        theme: asRecord(root.theme),
        brand: normalizeBrand(root),
        locale: asString(root.locale) ?? getCurrentAppLocale(),
    };
}
